import { Image as OrbitImage } from "./Image";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { isNil, mergeProps, slot, useRefState } from "../../shared";
import { jsx as _jsx } from "react/jsx-runtime";
const DefaultElement = "img";

function InnerAsyncImage({
  as = DefaultElement,
  children,
  delay = 250,
  forwardedRef,
  retryCount = 5,
  src,
  ...rest
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [failureCount, setFailureCount] = useState(0);
  const [imageRef, setImage] = useRefState();
  const [canRender, setCanRender] = useState(false);
  const [canRenderTimeoutIdRef, setCanRenderTimeoutId] = useRefState();

  if (retryCount < 1) {
    throw new Error("An async image retry count must be equal or greater to 1.");
  }

  const disposeImage = useCallback(() => {
    const image = imageRef.current;

    if (!isNil(image)) {
      image.onload = null;
      image.onerror = null;
      setImage(null);
    }
  }, [imageRef, setImage]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCanRender(true);
    }, delay);
    setCanRenderTimeoutId(timeoutId);
  }, [delay, setCanRenderTimeoutId]); // Reset when the image "src" change.

  useEffect(() => {
    setIsLoaded(false);
    setFailureCount(0);
  }, [src]);
  useEffect(() => {
    if (!isLoaded && failureCount < retryCount) {
      const image = new Image();
      image.src = src;

      image.onload = () => {
        disposeImage();
        setIsLoaded(true);

        if (!isNil(canRenderTimeoutIdRef.current)) {
          clearTimeout(canRenderTimeoutIdRef.current);
        }
      };

      image.onerror = () => {
        disposeImage();
        setFailureCount(failureCount + 1);
      };

      setImage(image);
      return () => {
        disposeImage();
      };
    }
  }, [src, retryCount, isLoaded, failureCount, setImage, disposeImage, canRenderTimeoutIdRef]);

  if (!canRender && !isLoaded) {
    return null;
  }

  if (isLoaded) {
    return /*#__PURE__*/_jsx(OrbitImage, { ...mergeProps(rest, {
        as,
        ref: forwardedRef,
        src
      })
    });
  }

  return children;
}

InnerAsyncImage.defaultElement = DefaultElement;
export const AsyncImage = slot("image", /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerAsyncImage, { ...props,
  forwardedRef: ref
})));